import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { flushSync } from "react-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { Plus, Trash2 } from "lucide-react";

import { HubMockupBadge } from "@/components/HubMockupBadge";
import { NotificationTopicOwnersSettingsSection } from "@/components/NotificationTopicOwnersSettingsSection";
import { Button } from "@/components/ui/Button";
import { HubSaveButton } from "@/components/ui/HubSaveButton";
import { HubSettingsSection } from "@/components/settings/HubSettingsSection";
import { HoverHintTrigger, PageTitleWithHelp, ReadOnlyHoverLayer } from "@/components/ui/title-help";
import { Input } from "@/components/ui/Input";
import { useHubModuleAdmin } from "@/lib/useHubModuleAdmin";
import { formatTimestamp } from "@/lib/utils";
import { fetchHubMockupEffective } from "@/lib/hubMockupApi";
import { ApiError, apiFetch } from "@/lib/api";

import {
  MANUFACTURING_HUB_SETTINGS_QUERY_KEY,
  fetchManufacturingHubSettings,
  patchManufacturingHubSettings,
  type ManufacturingInternalLaborMonthRate,
} from "./manufacturingSettingsApi";
import { InventoryDiscrepancyDistributionSection } from "./InventoryDiscrepancyDistributionSection";
import { LaserMarkPathsSettingsSection } from "./LaserMarkPathsSettingsSection";
import { PreEncapsulationSettingsSection } from "./PreEncapsulationSettingsSection";
import { ProductionCapacitySettingsSection } from "./ProductionCapacitySettingsSection";
import { TransferShippedNotificationSettingsSection } from "./TransferShippedNotificationSettingsSection";

const MANUFACTURING_STAFF_USERS_QUERY_KEY = ["manufacturing", "production-capacity", "staff-users"] as const;

function fetchManufacturingStaffUsers(): Promise<{
  users: Array<{ userId: string; email: string; displayName: string | null }>;
}> {
  return apiFetch("/api/manufacturing/production-capacity/staff/user-options");
}

interface MonthRowDraft {
  /** Stable key for React and row updates (not persisted). */
  id: string;
  yearMonth: string;
  laborDraft: string;
  overheadDraft: string;
  /** Row created in the browser and not yet persisted. */
  clientOnly?: boolean;
}

function newRowId(): string {
  if (typeof crypto !== "undefined" && "randomUUID" in crypto) {
    return crypto.randomUUID();
  }
  return `row-${String(Math.random()).slice(2)}`;
}

function utcYearMonthFromDate(d: Date): string {
  const y = d.getUTCFullYear();
  const m = String(d.getUTCMonth() + 1).padStart(2, "0");
  return `${String(y)}-${m}`;
}

function utcYearMonthNow(): string {
  return utcYearMonthFromDate(new Date());
}

function isMonthLocked(yearMonth: string): boolean {
  const trimmed = yearMonth.trim();
  if (!/^\d{4}-\d{2}$/.test(trimmed)) {
    return true;
  }
  return trimmed < utcYearMonthNow();
}

function addOneUtcMonth(yearMonth: string): string | null {
  const match = /^(\d{4})-(\d{2})$/.exec(yearMonth.trim());
  if (!match) return null;
  const y = Number(match[1]);
  const m = Number(match[2]);
  if (!Number.isFinite(y) || !Number.isFinite(m) || m < 1 || m > 12) return null;
  const d = new Date(Date.UTC(y, m - 1, 1));
  d.setUTCMonth(d.getUTCMonth() + 1);
  return utcYearMonthFromDate(d);
}

function rowsFromServer(rows: ManufacturingInternalLaborMonthRate[]): MonthRowDraft[] {
  return rows.map((row) => ({
    id: newRowId(),
    yearMonth: row.yearMonth,
    laborDraft: String(row.laborDollarsPerHour),
    overheadDraft: String(row.overheadDollarsPerHour),
  }));
}

function defaultNextMonth(existing: MonthRowDraft[]): string {
  if (existing.length === 0) {
    return utcYearMonthFromDate(new Date());
  }
  const sorted = [...existing].map((row) => row.yearMonth.trim()).sort((a, b) => b.localeCompare(a));
  const latest = sorted[0] ?? utcYearMonthFromDate(new Date());
  return addOneUtcMonth(latest) ?? utcYearMonthFromDate(new Date());
}

function formatUsdPerHour(value: number): string {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value);
}

function formatDraftMoney(draft: string): string {
  const n = Number.parseFloat(draft.trim());
  if (!Number.isFinite(n)) {
    return "—";
  }
  return formatUsdPerHour(n);
}

const manufacturingHubSettingsAdminTitle =
  "Module administrator access is required to change Manufacturing hub settings.";

export function ManufacturingSettingsPage() {
  const queryClient = useQueryClient();
  const hubManufacturingSettingsAllowed = useHubModuleAdmin("manufacturing");
  const canEditManufacturingManagement = useHubModuleAdmin("manufacturing_management");

  const settingsQuery = useQuery({
    queryKey: MANUFACTURING_HUB_SETTINGS_QUERY_KEY,
    queryFn: () => fetchManufacturingHubSettings(),
    staleTime: 15_000,
  });

  const hubManufacturingMockup = useQuery({
    queryKey: ["hub-mockup", "manufacturing", "settings"],
    queryFn: () => fetchHubMockupEffective("manufacturing", "settings"),
    staleTime: 60_000,
  });

  const [tableRows, setTableRows] = useState<MonthRowDraft[]>([]);
  const [settingsDirty, setSettingsDirty] = useState(false);
  const [editingRowId, setEditingRowId] = useState<string | null>(null);
  /** Snapshot for the active row edit session (see `beginRowEdit` / `cancelRowEdit`; mirrors MRP Assemblies row editing). */
  const editSessionRef = useRef<{ id: string; snapshot: MonthRowDraft } | null>(null);
  const tableRowsRef = useRef<MonthRowDraft[]>([]);
  useLayoutEffect(() => {
    tableRowsRef.current = tableRows;
  }, [tableRows]);

  useEffect(() => {
    if (!settingsQuery.data || settingsDirty || editingRowId !== null) {
      return;
    }
    setTableRows(rowsFromServer(settingsQuery.data.ratesByMonth));
  }, [settingsQuery.data, settingsDirty, editingRowId]);

  const sortedDisplayRows = useMemo(
    () => [...tableRows].sort((a, b) => a.yearMonth.localeCompare(b.yearMonth)),
    [tableRows],
  );

  const saveMutation = useMutation({
    mutationFn: () => {
      const ratesByMonth: ManufacturingInternalLaborMonthRate[] = [];
      const seen = new Set<string>();
      for (const row of sortedDisplayRows) {
        const ym = row.yearMonth.trim();
        if (ym === "") {
          throw new Error("Each row needs a month (YYYY-MM).");
        }
        if (!/^\d{4}-\d{2}$/.test(ym)) {
          throw new Error(`Invalid month "${ym}". Use YYYY-MM (for example 2026-05).`);
        }
        if (seen.has(ym)) {
          throw new Error(`Duplicate month ${ym}.`);
        }
        seen.add(ym);
        const labor = Number.parseFloat(row.laborDraft.trim());
        const overhead = Number.parseFloat(row.overheadDraft.trim());
        if (!Number.isFinite(labor) || !Number.isFinite(overhead)) {
          throw new Error("Labor and overhead must be numbers.");
        }
        ratesByMonth.push({
          yearMonth: ym,
          laborDollarsPerHour: labor,
          overheadDollarsPerHour: overhead,
        });
      }
      ratesByMonth.sort((a, b) => a.yearMonth.localeCompare(b.yearMonth));
      return patchManufacturingHubSettings({ ratesByMonth });
    },
    onSuccess: (data) => {
      void queryClient.setQueryData(MANUFACTURING_HUB_SETTINGS_QUERY_KEY, data);
      editSessionRef.current = null;
      setEditingRowId(null);
      setSettingsDirty(false);
      void queryClient.invalidateQueries({ queryKey: ["manufacturing", "work-orders"] });
    },
  });

  function cancelRowEdit(): void {
    const session = editSessionRef.current;
    editSessionRef.current = null;
    setEditingRowId(null);
    saveMutation.reset();
    if (session == null) {
      setSettingsDirty(false);
      return;
    }
    const { id, snapshot } = session;
    if (snapshot.clientOnly) {
      setTableRows((rows) => rows.filter((row) => row.id !== id));
    } else {
      setTableRows((rows) => rows.map((row) => (row.id === id ? snapshot : row)));
    }
    setSettingsDirty(false);
  }

  function beginRowEdit(row: MonthRowDraft): void {
    if (!hubManufacturingSettingsAllowed || isMonthLocked(row.yearMonth)) {
      return;
    }
    flushSync(() => {
      cancelRowEdit();
    });
    editSessionRef.current = {
      id: row.id,
      snapshot: {
        ...row,
        clientOnly: row.clientOnly,
      },
    };
    setEditingRowId(row.id);
    setSettingsDirty(true);
    saveMutation.reset();
  }

  function addRow(): void {
    if (!hubManufacturingSettingsAllowed) {
      return;
    }
    flushSync(() => {
      cancelRowEdit();
    });
    const id = newRowId();
    const nextMonth = defaultNextMonth(tableRowsRef.current);
    const newRow: MonthRowDraft = {
      id,
      yearMonth: nextMonth,
      laborDraft: "0",
      overheadDraft: "0",
      clientOnly: true,
    };
    editSessionRef.current = { id, snapshot: { ...newRow } };
    setTableRows((current) => [...current, newRow]);
    setEditingRowId(id);
    setSettingsDirty(true);
    saveMutation.reset();
  }

  function updateRow(rowId: string, patch: Partial<Pick<MonthRowDraft, "yearMonth" | "laborDraft" | "overheadDraft">>): void {
    if (patch.yearMonth !== undefined && patch.yearMonth !== "" && patch.yearMonth < utcYearMonthNow()) {
      return;
    }
    setTableRows((current) => current.map((row) => (row.id === rowId ? { ...row, ...patch } : row)));
  }

  function removeEditingRow(): void {
    const session = editSessionRef.current;
    if (session == null) {
      return;
    }
    const id = session.id;
    editSessionRef.current = null;
    setEditingRowId(null);
    saveMutation.reset();
    setTableRows((rows) => rows.filter((row) => row.id !== id));
    setSettingsDirty(false);
  }

  const tableBusy = settingsQuery.isLoading || saveMutation.isPending;
  const nowYm = utcYearMonthNow();
  const editingRow = tableRows.find((row) => row.id === editingRowId) ?? null;
  const editSnapshot = editSessionRef.current;
  const ratesRowDirty =
    editingRow != null &&
    editSnapshot != null &&
    editSnapshot.id === editingRow.id &&
    (editingRow.yearMonth !== editSnapshot.snapshot.yearMonth ||
      editingRow.laborDraft !== editSnapshot.snapshot.laborDraft ||
      editingRow.overheadDraft !== editSnapshot.snapshot.overheadDraft);

  return (
    <div className="space-y-4">
      <header>
        <div className="flex flex-wrap items-center gap-2">
          <PageTitleWithHelp hoverHint="Manufacturing management settings: transfer shipped email recipients by receiving location, inventory discrepancy and transfer quantity adjustment topic owners, LightBurn paths, Pre-Encapsulation allowlists, production capacity roster, and labor and overhead dollars per hour for work order costing.">
            Manufacturing management · Settings
          </PageTitleWithHelp>
          {hubManufacturingMockup.data?.bannerMockupVisible === true ? <HubMockupBadge /> : null}
        </div>
      </header>

      <TransferShippedNotificationSettingsSection />
      <InventoryDiscrepancyDistributionSection />
      <NotificationTopicOwnersSettingsSection
        topic="inventory_discrepancy"
        title="Inventory discrepancy topic owners"
        hoverHint="Hub users in this topic owner group receive in-app notifications when Picking submits a new SGS inventory discrepancy, and may claim ownership of open items."
        canEdit={canEditManufacturingManagement}
        userOptionsQueryKey={MANUFACTURING_STAFF_USERS_QUERY_KEY}
        fetchUserOptions={fetchManufacturingStaffUsers}
        readOnlyHoverHint="Manufacturing management administrators can edit this topic owner group."
      />
      <NotificationTopicOwnersSettingsSection
        topic="transfer_pack_adjustment"
        title="Transfer quantity adjustment topic owners"
        hoverHint="Hub users in this topic owner group receive in-app notifications for transfer packing quantity adjustment requests, and may claim ownership of open items."
        canEdit={canEditManufacturingManagement}
        userOptionsQueryKey={MANUFACTURING_STAFF_USERS_QUERY_KEY}
        fetchUserOptions={fetchManufacturingStaffUsers}
        readOnlyHoverHint="Manufacturing management administrators can edit this topic owner group."
      />
      <LaserMarkPathsSettingsSection />

      <HubSettingsSection
        title="Internal labor rates by month"
        hoverHint="Persisted as workflow.app_settings.hub.manufacturing under ratesByMonth. Save replaces the entire table. Months before the current UTC month are read-only for everyone."
        collapsible
        defaultCollapsed
      >
          {settingsQuery.isError ? (
            <div className="rounded-md border border-rose-200 bg-rose-50 p-3 text-sm text-rose-900">
              <p className="font-semibold">Could not load settings</p>
              <p className="mt-1 font-mono text-xs text-rose-800">
                {settingsQuery.error instanceof ApiError
                  ? `HTTP ${String(settingsQuery.error.status)}: ${settingsQuery.error.message}`
                  : settingsQuery.error instanceof Error
                    ? settingsQuery.error.message
                    : String(settingsQuery.error)}
              </p>
              {settingsQuery.error instanceof ApiError && settingsQuery.error.status === 404 ? (
                <p className="mt-2 text-xs text-rose-800">
                  The browser requested this app origin (<span className="font-mono">/api/manufacturing/settings</span>
                  ). In local development, Vite proxies that path to the integrator API (default{" "}
                  <span className="font-mono">http://localhost:8123</span>). A 404 means no route matched: the proxy
                  target may be the wrong host, or the running API build does not register this path. If you use a
                  non-default backend, set <span className="font-mono">VITE_API_ORIGIN</span> in{" "}
                  <span className="font-mono">ui/.env</span> to the correct API base URL.
                </p>
              ) : null}
              {settingsQuery.error instanceof ApiError && settingsQuery.error.status === 403 ? (
                <p className="mt-2 text-xs text-rose-800">
                  If you are not integrator role id 1, ask an administrator to grant the Manufacturing module and the
                  administrator tier for hub settings under{" "}
                  <Link to="/admin/module-access" className="font-medium text-brand-800 underline">
                    Admin → Module access
                  </Link>
                  .
                </p>
              ) : null}
            </div>
          ) : null}

          <div className="max-w-4xl overflow-x-auto rounded-md border border-slate-200">
            <table className="min-w-full divide-y divide-slate-200 text-sm">
              <thead className="bg-slate-50 text-left text-xs font-semibold uppercase tracking-wide text-slate-600">
                <tr>
                  <th className="px-3 py-2">Month (UTC)</th>
                  <th className="px-3 py-2">Labor ($/hour)</th>
                  <th className="px-3 py-2">Overhead ($/hour)</th>
                  <th className="px-3 py-2 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 bg-white">
                {settingsQuery.isLoading && !settingsQuery.data ? (
                  <tr>
                    <td colSpan={4} className="px-3 py-8 text-center text-slate-500">
                      Loading…
                    </td>
                  </tr>
                ) : sortedDisplayRows.length === 0 ? (
                  <tr>
                    <td colSpan={4} className="px-3 py-8 text-center text-slate-500">
                      No months yet. Use &quot;Add month&quot; below to create a row.
                    </td>
                  </tr>
                ) : (
                  sortedDisplayRows.map((row) => {
                    const locked = isMonthLocked(row.yearMonth);
                    const editing = editingRowId === row.id;
                    return (
                      <tr key={row.id}>
                        <td className="px-3 py-2 align-middle">
                          {editing ? (
                            <Input
                              type="month"
                              className="max-w-[11rem] font-mono text-sm"
                              min={nowYm}
                              value={row.yearMonth.length >= 7 ? row.yearMonth.slice(0, 7) : row.yearMonth}
                              onChange={(event) => {
                                const value = event.target.value;
                                if (value !== "" && value < utcYearMonthNow()) {
                                  return;
                                }
                                updateRow(row.id, { yearMonth: value });
                              }}
                              disabled={!hubManufacturingSettingsAllowed || tableBusy}
                            />
                          ) : (
                            <span className="whitespace-nowrap font-mono text-slate-900">{row.yearMonth}</span>
                          )}
                        </td>
                        <td className="px-3 py-2 align-middle">
                          {editing ? (
                            <Input
                              type="number"
                              min={0}
                              step="0.01"
                              className="max-w-[8rem] tabular-nums"
                              value={row.laborDraft}
                              onChange={(event) => updateRow(row.id, { laborDraft: event.target.value })}
                              disabled={!hubManufacturingSettingsAllowed || tableBusy}
                              inputMode="decimal"
                              autoComplete="off"
                            />
                          ) : (
                            <span className="tabular-nums text-slate-900">{formatDraftMoney(row.laborDraft)}</span>
                          )}
                        </td>
                        <td className="px-3 py-2 align-middle">
                          {editing ? (
                            <Input
                              type="number"
                              min={0}
                              step="0.01"
                              className="max-w-[8rem] tabular-nums"
                              value={row.overheadDraft}
                              onChange={(event) => updateRow(row.id, { overheadDraft: event.target.value })}
                              disabled={!hubManufacturingSettingsAllowed || tableBusy}
                              inputMode="decimal"
                              autoComplete="off"
                            />
                          ) : (
                            <span className="tabular-nums text-slate-900">{formatDraftMoney(row.overheadDraft)}</span>
                          )}
                        </td>
                        <td className="px-3 py-2 text-right align-middle">
                          {editing ? (
                            <div className="flex flex-wrap items-center justify-end gap-2">
                              {!locked ? (
                                <HoverHintTrigger
                                  trigger={
                                    <button
                                      type="button"
                                      className="inline-flex rounded-md p-1.5 text-slate-500 hover:bg-rose-50 hover:text-rose-700"
                                      onClick={() => removeEditingRow()}
                                      disabled={!hubManufacturingSettingsAllowed || tableBusy}
                                      aria-label={`Remove ${row.yearMonth} from the table`}
                                    >
                                      <Trash2 className="h-4 w-4" aria-hidden />
                                    </button>
                                  }
                                >
                                  Remove this row from the table
                                </HoverHintTrigger>
                              ) : null}
                              <Button size="sm" variant="secondary" onClick={() => cancelRowEdit()} disabled={tableBusy}>
                                Cancel
                              </Button>
                              <HubSaveButton
                                size="sm"
                                dirty={ratesRowDirty}
                                disabled={!hubManufacturingSettingsAllowed || tableBusy}
                                blockedHint={manufacturingHubSettingsAdminTitle}
                                isLoading={saveMutation.isPending}
                                onClick={() => saveMutation.mutate()}
                              >
                                Save
                              </HubSaveButton>
                            </div>
                          ) : locked ? (
                            <span className="text-xs text-slate-400">—</span>
                          ) : (
                            <ReadOnlyHoverLayer active={!hubManufacturingSettingsAllowed} hoverHint={manufacturingHubSettingsAdminTitle} layout="inline">
                              <Button size="sm" variant="secondary" disabled={!hubManufacturingSettingsAllowed} onClick={() => beginRowEdit(row)}>
                                Edit
                              </Button>
                            </ReadOnlyHoverLayer>
                          )}
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>

          <div className="flex flex-wrap items-center gap-3">
            <ReadOnlyHoverLayer active={!hubManufacturingSettingsAllowed} hoverHint={manufacturingHubSettingsAdminTitle} layout="inline">
              <Button type="button" variant="secondary" onClick={() => addRow()} disabled={!hubManufacturingSettingsAllowed || tableBusy}>
                <Plus className="h-4 w-4" aria-hidden /> Add month
              </Button>
            </ReadOnlyHoverLayer>
            {settingsQuery.data?.updatedAt ? (
              <span className="text-xs text-slate-500">Last saved {formatTimestamp(settingsQuery.data.updatedAt)}</span>
            ) : null}
          </div>
          {saveMutation.isError ? (
            <p className="text-sm text-rose-600">
              {saveMutation.error instanceof Error ? saveMutation.error.message : "Save failed."}
            </p>
          ) : null}
          {!hubManufacturingSettingsAllowed ? (
            <p className="text-sm text-slate-600">You do not have write access to Manufacturing hub settings.</p>
          ) : null}
      </HubSettingsSection>

      <PreEncapsulationSettingsSection />

      <ProductionCapacitySettingsSection />
    </div>
  );
}
